import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { format, parseISO, addDays } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { ScheduleGrid, ScheduleLegend } from "@/components/ScheduleGrid";
import { DateStrip } from "@/components/DateStrip";
import {
  api,
  formatEuro,
  formatHour,
  toDateString,
  STUDIO_LABELS,
  STUDIO_SUBTITLES,
  STUDIO_LOGOS,
  CATEGORY_LABELS,
} from "@/lib/api";
import { SITE, IS_STUDIO, SITE_STUDIO_TYPE } from "@/config";
import { OldPrice, PromoBadge } from "@/components/PromoPrice";
import { useSiteTexts } from "@/hooks/useSiteTexts";
import {
  Mic,
  Camera,
  Image as ImageIcon,
  Video,
  Radio,
  Briefcase,
  Disc3,
  Layers,
  Crown,
  Wand2,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  Minus,
  Plus,
  CreditCard,
  Banknote,
  CheckCircle2,
  CalendarDays,
  Loader2,
  Hourglass,
  Mail,
  Info,
  Music,
  PenLine,
  Lightbulb,
  Check,
  Plane,
  Users,
} from "lucide-react";

const SERVICE_ICONS = {
  mic: Mic,
  camera: Camera,
  image: ImageIcon,
  video: Video,
  radio: Radio,
  briefcase: Briefcase,
  disc: Disc3,
  layers: Layers,
  crown: Crown,
  wand: Wand2,
  sliders: SlidersHorizontal,
  music: Music,
  pen: PenLine,
  lightbulb: Lightbulb,
  plane: Plane,
  users: Users,
};

const STEPS = ["Espace", "Service", "Date & heures", "Coordonnées", "Paiement"];
const CATEGORY_ORDER = ["studio", "beatmaking_catalogue"];

const formatEndHour = (h) => (h >= 24 ? `${formatHour(h - 24)} (+1j)` : formatHour(h));

const COUNTABLE_ADDON_SLUGS = ["supplement-figurant", "supplement-danseur"];

const tiersLabel = (tiers) =>
  Object.entries(tiers)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([h, p]) => `${h}h ${p}€`)
    .join(" · ");

export default function Reserver() {
  const texts = useSiteTexts();
  const [searchParams] = useSearchParams();
  const prefillDate = searchParams.get("date");
  const prefillHour = searchParams.get("hour");

  // Le site est dédié à un seul espace : pas d'étape de choix d'espace
  const studio = SITE_STUDIO_TYPE;
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [service, setService] = useState(null);
  const [date, setDate] = useState(prefillDate || toDateString(new Date()));
  const [startHour, setStartHour] = useState(prefillHour !== null ? parseInt(prefillHour, 10) : null);
  const [duration, setDuration] = useState(1);
  const [slots, setSlots] = useState([]);
  const [nextSlots, setNextSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [customer, setCustomer] = useState({ name: "", email: "", phone: "" });
  const [addons, setAddons] = useState([]);
  const [addonQty, setAddonQty] = useState({});
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(null);
  const [confirmedBooking, setConfirmedBooking] = useState(null);

  useEffect(() => {
    if (!studio) return;
    api
      .get("/services", { params: { studio_type: studio } })
      .then((res) => setServices(res.data))
      .catch(() => toast.error("Impossible de charger les services"));
  }, [studio]);

  const fetchSlots = useCallback(async () => {
    if (!studio || !date) return;
    setSlotsLoading(true);
    try {
      const nextDate = toDateString(addDays(parseISO(date), 1));
      const [res, resNext] = await Promise.all([
        api.get("/availability", { params: { studio_type: studio, date } }),
        api.get("/availability", { params: { studio_type: studio, date: nextDate } }),
      ]);
      setSlots(res.data.slots);
      setNextSlots(resNext.data.slots);
    } catch (e) {
      setSlots([]);
      setNextSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [studio, date]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  const effectiveDuration = useMemo(() => {
    if (service && service.pricing_type === "fixed") return service.duration_hours || 1;
    return duration;
  }, [service, duration]);

  const selectedHours = useMemo(() => {
    if (startHour === null) return [];
    return Array.from({ length: effectiveDuration }, (_, i) => startHour + i);
  }, [startHour, effectiveDuration]);

  const crossesMidnight = startHour !== null && startHour + effectiveDuration > 24;

  const rangeValid = useMemo(() => {
    if (startHour === null) return false;
    const now = new Date();
    const todayStr = toDateString(now);
    if (date === todayStr && startHour <= now.getHours()) return false;
    return selectedHours.every((h) => {
      if (h < 24) {
        const slot = slots.find((s) => s.hour === h);
        return slot && slot.status === "libre";
      }
      const slot = nextSlots.find((s) => s.hour === h - 24);
      return slot && slot.status === "libre";
    });
  }, [startHour, selectedHours, slots, nextSlots, date]);

  useEffect(() => {
    if (!service || startHour === null) {
      setQuote(null);
      return;
    }
    api
      .post("/quote", {
        service_id: service.id,
        date,
        start_hour: startHour,
        duration_hours: effectiveDuration,
        addons,
        addon_quantities: addonQty,
        customer: { name: "x", email: "x@x.fr", phone: "0" },
      })
      .then((res) => setQuote(res.data))
      .catch(() => setQuote(null));
  }, [service, startHour, effectiveDuration, date, addons, addonQty]);

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email);
  const customerValid = customer.name.trim().length >= 2 && emailValid && customer.phone.trim().length >= 6;

  const canGoNext = () => {
    if (step === 1) return !!service;
    if (step === 2) return rangeValid;
    if (step === 3) return customerValid;
    return false;
  };

  const goNext = () => {
    if (step === 3 && !customerValid) {
      toast.error("Veuillez remplir correctement vos coordonnées");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const payload = () => ({
    service_id: service.id,
    date,
    start_hour: startHour,
    duration_hours: effectiveDuration,
    addons,
    addon_quantities: addonQty,
    customer,
  });

  const complementServices = useMemo(
    () => services.filter((s) => s.category === (IS_STUDIO ? "complement" : "media_supplement")),
    [services]
  );
  const addonsAllowed = IS_STUDIO
    ? service?.slug === "session-enregistrement"
    : service?.category === "studio";

  const toggleAddon = (slug) => {
    setAddons((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  };

  const changeAddonQty = (slug, delta) => {
    setAddonQty((prev) => {
      const current = prev[slug] || 1;
      const next = Math.max(1, Math.min(10, current + delta));
      return { ...prev, [slug]: next };
    });
  };

  const handlePayOnline = async () => {
    setSubmitting("online");
    try {
      const res = await api.post("/checkout/create", {
        ...payload(),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur lors de la création du paiement";
      toast.error(msg);
      if (e.response?.status === 409) fetchSlots();
      setSubmitting(null);
    }
  };

  const handleReserveOnsite = async () => {
    setSubmitting("onsite");
    try {
      const res = await api.post("/bookings", payload());
      setConfirmedBooking(res.data);
      toast.success(
        res.data.status === "en_attente_validation"
          ? "Réservation reçue ! En attente de validation."
          : "Réservation confirmée !"
      );
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur lors de la réservation";
      toast.error(msg);
      if (e.response?.status === 409) fetchSlots();
    } finally {
      setSubmitting(null);
    }
  };

  const groupedServices = useMemo(() => {
    const groups = [];
    for (const cat of CATEGORY_ORDER) {
      const items = services.filter((s) => (s.category || "studio") === cat);
      if (items.length > 0) groups.push({ category: cat, items });
    }
    return groups;
  }, [services]);

  // ---------- Confirmation screen (onsite) ----------
  if (confirmedBooking) {
    const pending = confirmedBooking.status === "en_attente_validation";
    return (
      <div className="mx-auto w-full max-w-2xl px-4 py-16 sm:px-6">
        <Card className="border-primary/40 bg-card" data-testid="onsite-confirmation-card">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-3">
              {pending ? (
                <Hourglass className="h-10 w-10 text-primary" />
              ) : (
                <CheckCircle2 className="h-10 w-10 text-primary" />
              )}
              <div>
                <h1 className="font-heading text-2xl font-semibold">
                  {pending ? "Réservation reçue !" : "Réservation confirmée !"}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {pending
                    ? "Votre demande est en attente de validation par notre équipe."
                    : "Règlement sur place le jour de la session."}
                </p>
              </div>
            </div>
            <div className="mt-5 flex items-start gap-2.5 rounded-lg border border-primary/30 bg-[hsl(var(--studio-yellow)/0.06)] px-4 py-3">
              <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
              <p className="text-sm text-foreground/90" data-testid="confirmation-email-notice">
                Un email {pending ? "de réception" : "de confirmation"} vous a été envoyé à{" "}
                <span className="font-medium">{confirmedBooking.customer?.email}</span>.
                {pending && " Vous recevrez un second email dès validation."}
              </p>
            </div>
            <Separator className="my-6" />
            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Référence</span>
                <span className="font-mono-nums text-xs">{confirmedBooking.id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Espace</span>
                <span className="font-medium">{STUDIO_LABELS[confirmedBooking.studio_type]}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Service</span>
                <span className="font-medium">{confirmedBooking.service_name}</span>
              </div>
              {(confirmedBooking.addons || []).length > 0 && (
                <div className="flex justify-between" data-testid="confirmation-addons">
                  <span className="text-muted-foreground">Compléments</span>
                  <span className="max-w-[220px] text-right text-xs font-medium">
                    {confirmedBooking.addons.map((a) => a.name).join(" + ")}
                  </span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date</span>
                <span className="font-medium capitalize">
                  {format(parseISO(confirmedBooking.date), "EEEE d MMMM yyyy", { locale: fr })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Horaire</span>
                <span className="font-mono-nums font-medium">
                  {formatHour(confirmedBooking.start_hour)} – {formatHour(confirmedBooking.end_hour)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">À régler sur place</span>
                <span className="font-mono-nums text-lg font-semibold text-primary" data-testid="onsite-confirmation-amount">
                  {formatEuro(confirmedBooking.amount)}
                </span>
              </div>
            </div>
            <p className="mt-5 rounded-md border border-border bg-secondary/50 px-4 py-3 text-xs text-muted-foreground">
              Rappel : annulation gratuite jusqu'à 24h avant la session. Paiement sur place : espèces
              ou carte bancaire acceptées au studio.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild data-testid="confirmation-back-planning">
                <Link to="/planning">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  Voir le planning
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <Link to="/">Retour à l'accueil</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- Booking flow ----------
  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 sm:py-14 lg:px-8">
      <h1 className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl">
        {texts.reserver_title || "Réserver"} <span className="text-primary">{SITE.fullName}</span>
      </h1>

      <ol className="mt-8 flex flex-wrap items-center gap-2" data-testid="booking-stepper">
        {STEPS.slice(1).map((label, idx) => {
          const i = idx + 1;
          return (
            <li key={label} className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => i < step && setStep(i)}
                className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                  i === step
                    ? "border-primary bg-primary text-primary-foreground"
                    : i < step
                    ? "border-primary/40 text-primary hover:bg-primary/10"
                    : "border-border text-muted-foreground"
                }`}
                data-testid={`stepper-step-${i}`}
              >
                <span className="font-mono-nums">{idx + 1}</span>
                <span className="hidden sm:inline">{label}</span>
              </button>
              {i < STEPS.length - 1 && <span className="h-px w-4 bg-border" />}
            </li>
          );
        })}
      </ol>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div>
          {/* STEP 1: Service (groupé par catégorie) */}
          {step === 1 && (
            <div className="space-y-8" data-testid="step-service">
              {services.length === 0 && (
                <p className="text-sm text-muted-foreground">Chargement des services…</p>
              )}
              {groupedServices.map((group) => (
                <div key={group.category}>
                  {groupedServices.length > 1 && (
                    <h3 className="mb-3 font-heading text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      {CATEGORY_LABELS[group.category] || group.category}
                    </h3>
                  )}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {group.items.map((svc) => {
                      const Icon = SERVICE_ICONS[svc.icon] || Mic;
                      const isSelected = service?.id === svc.id;
                      return (
                        <button
                          key={svc.id}
                          type="button"
                          onClick={() => {
                            setService(svc);
                            setAddons([]);
                            if (svc.pricing_type === "fixed") setDuration(svc.duration_hours || 1);
                            else setDuration(1);
                          }}
                          className={`rounded-xl border p-5 text-left transition-colors duration-150 ${
                            isSelected
                              ? "border-primary bg-[hsl(var(--studio-yellow)/0.08)]"
                              : "border-border bg-card hover:border-primary/50"
                          }`}
                          data-testid={`service-option-${svc.slug}`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[hsl(var(--studio-yellow)/0.12)]">
                              <Icon className="h-5 w-5 text-primary" />
                            </span>
                            {svc.pricing_type === "fixed" && svc.duration_hours && (
                              <Badge variant="outline" className="border-border text-muted-foreground">
                                {svc.duration_hours}h
                              </Badge>
                            )}
                          </div>
                          <h3 className="mt-3 font-heading text-base font-semibold">{svc.name}</h3>
                          <p className="mt-1 text-xs text-muted-foreground">{svc.description}</p>
                          <div className="mt-3 flex flex-wrap items-baseline gap-2">
                            <span className="font-mono-nums font-semibold text-primary">
                              {svc.from_price && (
                                <span className="mr-1 text-[11px] font-normal text-muted-foreground">
                                  à partir de
                                </span>
                              )}
                              {svc.promo_live && (
                                <s className="mr-1 text-xs font-normal text-muted-foreground">
                                  {formatEuro(svc.price_tiers_before ? svc.price_tiers_before["1"] : svc.price_before)}
                                </s>
                              )}
                              {formatEuro(svc.price_tiers ? svc.price_tiers["1"] : svc.price_onsite)}
                              {svc.pricing_type === "hourly" ? "/h" : ""}
                            </span>
                            <PromoBadge svc={svc} />
                            {svc.price_tiers && (
                              <span className="text-[11px] text-muted-foreground">
                                tarif dégressif · {tiersLabel(svc.price_tiers)}
                              </span>
                            )}
                            <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                              en ligne ou sur place
                            </span>
                          </div>
                          {!IS_STUDIO && (
                            <span className="mt-2 block text-[11px] text-muted-foreground" data-testid={`booking-supplements-hint-${svc.slug}`}>
                              + Suppléments au choix à l'étape suivante : figurant(e)s, danseur(se)s, garage auto
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* STEP 2: Date & hours */}
          {step === 2 && (
            <div data-testid="step-datetime">
              <DateStrip
                selectedDate={date}
                onSelect={(d) => {
                  setDate(d);
                  setStartHour(null);
                }}
              />
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  Sélectionnez l'heure de début
                  {service?.pricing_type === "hourly" ? ", puis ajustez la durée" : ""}
                </p>
                <ScheduleLegend />
              </div>
              <div className="mt-4">
                {slotsLoading ? (
                  <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                    {Array.from({ length: 24 }).map((_, i) => (
                      <Skeleton key={i} className="h-[58px] rounded-md" />
                    ))}
                  </div>
                ) : (
                  <ScheduleGrid
                    slots={slots}
                    date={date}
                    selectedHours={selectedHours}
                    onSlotClick={(hour) => setStartHour(hour)}
                  />
                )}
              </div>

              {service?.pricing_type === "hourly" && (
                <div className="mt-6 rounded-xl border border-border bg-card p-5">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <Label className="text-sm font-medium">Durée de la session</Label>
                      <p className="mt-1 text-xs text-muted-foreground">
                        {service.price_tiers
                          ? `Tarif dégressif : ${tiersLabel(service.price_tiers)}`
                          : "Choisissez le nombre d'heures"}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setDuration((d) => Math.max(1, d - 1))}
                        disabled={duration <= 1}
                        data-testid="duration-decrease"
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-16 text-center font-mono-nums text-xl font-semibold" data-testid="duration-value">
                        {duration}h
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={() => setDuration((d) => Math.min(20, d + 1))}
                        disabled={duration >= 20}
                        data-testid="duration-increase"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {crossesMidnight && (
                    <p className="mt-3 text-xs leading-relaxed text-primary" data-testid="overnight-notice">
                      Créneau de nuit : votre session se termine le lendemain à{" "}
                      {formatEndHour(startHour + effectiveDuration)}. Les heures après minuit sont
                      automatiquement réservées sur la journée suivante.
                    </p>
                  )}
                  {duration >= 8 && (
                    <p className="mt-3 text-xs leading-relaxed text-muted-foreground" data-testid="long-session-hint">
                      Vos sessions peuvent traverser minuit — par exemple de 21h à 02h du matin.
                    </p>
                  )}
                </div>
              )}

              {addonsAllowed && complementServices.length > 0 && (
                <div className="mt-6 rounded-xl border border-border bg-card p-5" data-testid="addons-panel">
                  <Label className="text-sm font-medium">
                    {IS_STUDIO ? "Compléments (optionnel)" : "Suppléments tournage (optionnel)"}
                  </Label>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {IS_STUDIO
                      ? "Ajoutez de la post-production ou un accompagnement musical à votre session."
                      : "Donnez du caractère à votre tournage : décor garage auto, figurant(e)s, danseur(se)s…"}
                  </p>
                  {(IS_STUDIO
                    ? [
                        { label: "Post-production", slugs: ["mixage", "mastering", "mix-master"] },
                        { label: "Accompagnement musical", slugs: ["topline", "ghostwriting", "conseils-session"] },
                      ]
                    : [{ label: "Suppléments", slugs: complementServices.map((s) => s.slug) }]
                  ).map((group) => {
                    const items = complementServices.filter((s) => group.slugs.includes(s.slug));
                    if (items.length === 0) return null;
                    return (
                      <div key={group.label} className="mt-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                          {group.label}
                        </p>
                        <div className="mt-2 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                          {items.map((svc) => {
                      const Icon = SERVICE_ICONS[svc.icon] || SlidersHorizontal;
                      const selected = addons.includes(svc.slug);
                      const countable = COUNTABLE_ADDON_SLUGS.includes(svc.slug);
                      const qty = addonQty[svc.slug] || 1;
                      return (
                        <div key={svc.slug} className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => toggleAddon(svc.slug)}
                            className={`flex flex-1 items-start gap-3 rounded-lg border p-3.5 text-left transition-colors duration-150 ${
                              selected
                                ? "border-primary bg-[hsl(var(--studio-yellow)/0.08)]"
                                : "border-border hover:border-primary/50"
                            }`}
                            data-testid={`addon-option-${svc.slug}`}
                          >
                            <span
                              className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border ${
                                selected ? "border-primary bg-primary" : "border-border"
                              }`}
                            >
                              {selected && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                            </span>
                            <span className="flex-1">
                              <span className="flex items-center gap-1.5 text-sm font-medium">
                                <Icon className="h-3.5 w-3.5 text-primary" />
                                {svc.name}
                              </span>
                              <span className="mt-0.5 block text-[11px] text-muted-foreground">{svc.description}</span>
                            </span>
                            <span className="font-mono-nums text-sm font-semibold text-primary">
                              {svc.from_price && (
                                <span className="block text-right text-[10px] font-normal text-muted-foreground">
                                  à partir de
                                </span>
                              )}
                              <OldPrice svc={svc} className="text-[11px]" />
                              {formatEuro(svc.price_onsite)}
                              {countable && (
                                <span className="block text-right text-[10px] font-normal text-muted-foreground">
                                  / pers.
                                </span>
                              )}
                            </span>
                          </button>
                          {selected && countable && (
                            <div
                              className="mt-1.5 flex items-center justify-between rounded-lg border border-primary/30 bg-[hsl(var(--studio-yellow)/0.05)] px-3 py-2"
                              data-testid={`addon-qty-${svc.slug}`}
                            >
                              <span className="text-xs font-medium">Nombre de personnes</span>
                              <div className="flex items-center gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => changeAddonQty(svc.slug, -1)}
                                  disabled={qty <= 1}
                                  data-testid={`addon-qty-decrease-${svc.slug}`}
                                >
                                  <Minus className="h-3.5 w-3.5" />
                                </Button>
                                <span className="w-8 text-center font-mono-nums text-sm font-semibold" data-testid={`addon-qty-value-${svc.slug}`}>
                                  {qty}
                                </span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="icon"
                                  className="h-7 w-7"
                                  onClick={() => changeAddonQty(svc.slug, 1)}
                                  disabled={qty >= 10}
                                  data-testid={`addon-qty-increase-${svc.slug}`}
                                >
                                  <Plus className="h-3.5 w-3.5" />
                                </Button>
                                <span className="ml-1 font-mono-nums text-xs font-semibold text-primary">
                                  = {formatEuro(svc.price_onsite * qty)}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                        </div>
                      </div>
                    );
                  })}
                  <p className="mt-3 text-[11px] text-muted-foreground">
                    {IS_STUDIO
                      ? "Les compléments se réservent uniquement avec une session d'enregistrement (1h minimum)."
                      : "Vous cherchez des figurants spécifiques ou un danseur / une danseuse différente ? Écrivez-nous à fnkfilms.pro@gmail.com avec la description des profils recherchés — nous organisons le casting."}
                  </p>
                </div>
              )}

              {startHour !== null && !rangeValid && (
                <p className="mt-4 text-sm text-destructive" data-testid="range-invalid-notice">
                  Ce créneau n'est pas entièrement disponible ({formatHour(startHour)} →{" "}
                  {formatEndHour(startHour + effectiveDuration)}). Choisissez une autre heure de
                  début ou réduisez la durée.
                </p>
              )}
            </div>
          )}

          {/* STEP 3: Customer info */}
          {step === 3 && (
            <div className="max-w-md space-y-5" data-testid="step-customer">
              <div className="space-y-2">
                <Label htmlFor="customer-name">Nom complet</Label>
                <Input
                  id="customer-name"
                  placeholder="Jean Dupont"
                  value={customer.name}
                  onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                  data-testid="customer-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-email">Email</Label>
                <Input
                  id="customer-email"
                  type="email"
                  placeholder="jean@exemple.fr"
                  value={customer.email}
                  onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                  data-testid="customer-email-input"
                />
                {customer.email && !emailValid && (
                  <p className="text-xs text-destructive">Adresse email invalide</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Votre confirmation de réservation sera envoyée à cette adresse.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="customer-phone">Téléphone</Label>
                <Input
                  id="customer-phone"
                  type="tel"
                  placeholder="06 12 34 56 78"
                  value={customer.phone}
                  onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                  data-testid="customer-phone-input"
                />
              </div>
            </div>
          )}

          {/* STEP 4: Payment choice */}
          {step === 4 && quote && (
            <div data-testid="step-payment">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Card className="border-primary bg-[hsl(var(--studio-yellow)/0.06)]" data-testid="price-option-pay-online">
                  <CardContent className="flex h-full flex-col p-6">
                    <div className="flex items-center justify-between">
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary">
                        <CreditCard className="h-5 w-5 text-primary-foreground" />
                      </span>
                      <Badge className="bg-primary text-primary-foreground">
                        Confirmation immédiate
                      </Badge>
                    </div>
                    <h3 className="mt-4 font-heading text-lg font-semibold text-primary">Payer en ligne</h3>
                    <p className="mt-1 flex-1 text-sm text-muted-foreground">
                      Paiement sécurisé via Stripe. Votre créneau est confirmé définitivement, notre
                      équipe est prévenue par email.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="payment-methods-badges">
                      {["CB / Visa / Mastercard", "Apple Pay", "Google Pay", "Virement bancaire"].map((m) => (
                        <span
                          key={m}
                          className="rounded-full border border-primary/30 bg-[hsl(var(--studio-yellow)/0.08)] px-2.5 py-1 text-[11px] font-medium text-foreground/90"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                    <p className="mt-4 font-mono-nums text-3xl font-semibold text-primary" data-testid="online-total">
                      {formatEuro(quote.amount_online)}
                    </p>
                    <Button
                      className="mt-5 w-full"
                      size="lg"
                      onClick={handlePayOnline}
                      disabled={submitting !== null}
                      data-testid="stripe-checkout-button"
                    >
                      {submitting === "online" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="mr-2 h-4 w-4" />
                      )}
                      Payer {formatEuro(quote.amount_online)}
                    </Button>
                  </CardContent>
                </Card>

                <Card className="border-border bg-card" data-testid="price-option-pay-onsite">
                  <CardContent className="flex h-full flex-col p-6">
                    <div className="flex items-center justify-between">
                      <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary">
                        <Banknote className="h-5 w-5 text-foreground" />
                      </span>
                      <Badge variant="outline" className="border-border text-muted-foreground">
                        Même tarif
                      </Badge>
                    </div>
                    <h3 className="mt-4 font-heading text-lg font-semibold">Payer sur place</h3>
                    <p className="mt-1 flex-1 text-sm text-muted-foreground">
                      Réservez sans paiement immédiat. Votre demande est validée par notre équipe et
                      confirmée par email. Règlement au studio le jour de la session, en espèces ou
                      par carte bancaire.
                    </p>
                    <p className="mt-4 font-mono-nums text-3xl font-semibold" data-testid="onsite-total">
                      {formatEuro(quote.amount_onsite)}
                    </p>
                    <Button
                      className="mt-5 w-full"
                      size="lg"
                      variant="outline"
                      onClick={handleReserveOnsite}
                      disabled={submitting !== null}
                      data-testid="onsite-reserve-button"
                    >
                      {submitting === "onsite" ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Banknote className="mr-2 h-4 w-4" />
                      )}
                      Réserver, payer sur place
                    </Button>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-5 flex items-start gap-2.5 rounded-lg border border-border bg-secondary/40 px-4 py-3" data-testid="payment-policy-notice">
                <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <div className="text-xs leading-relaxed text-muted-foreground">
                  <p>
                    <span className="font-medium text-foreground">Conditions d'annulation :</span>{" "}
                    annulation gratuite jusqu'à 24h avant la session (remboursement intégral en ligne).
                    Passé ce délai ou en cas d'absence, la session reste due et sera encaissée.
                  </p>
                  <p className="mt-1.5" data-testid="online-noshow-notice">
                    <span className="font-medium text-foreground">Paiement en ligne (débité immédiatement) :</span>{" "}
                    en cas d'empêchement, vous devez prévenir au moins{" "}
                    <span className="font-medium text-primary">2 heures avant</span> la session, sinon
                    le paiement sera encaissé sans aucun remboursement dû.
                  </p>
                  <p className="mt-1.5" data-testid="studio-refund-notice">
                    <span className="font-medium text-foreground">Annulation par le studio :</span>{" "}
                    votre paiement en ligne est automatiquement et intégralement remboursé,
                    conformément aux règles bancaires (délai habituel : 5 à 10 jours ouvrés).
                  </p>
                  <p className="mt-1.5">
                    <span className="font-medium text-foreground">Paiement sur place :</span> espèces ou
                    carte bancaire acceptées au studio. Ces conditions vous seront rappelées par email.
                  </p>
                  {studio === "enregistrement" && (
                    <p className="mt-1.5" data-testid="album-quote-notice">
                      <span className="font-medium text-foreground">Projet / album complet :</span>{" "}
                      uniquement sur devis, et uniquement par mail à{" "}
                      <a
                        href="mailto:fnkfilms.pro@gmail.com"
                        className="font-medium text-primary underline-offset-2 hover:underline"
                      >
                        fnkfilms.pro@gmail.com
                      </a>
                      .
                    </p>
                  )}
                  {studio === "media" && (
                    <p className="mt-1.5" data-testid="media-outfits-notice">
                      <span className="font-medium text-foreground">Photo / vidéo :</span> les tenues ne
                      sont pas fournies. Toute prestation supplémentaire sera facturée en fonction de
                      vos demandes.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {step < 4 && (
            <div className="mt-8 flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={() => setStep((s) => Math.max(1, s - 1))}
                disabled={step === 1}
                data-testid="booking-stepper-back-button"
              >
                <ChevronLeft className="mr-1 h-4 w-4" />
                Retour
              </Button>
              <Button onClick={goNext} disabled={!canGoNext()} data-testid="booking-stepper-next-button">
                Continuer
                <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Sticky recap */}
        <div className="lg:sticky lg:top-24 lg:self-start">
          <Card className="border-border bg-card" data-testid="booking-recap">
            <CardContent className="p-5 sm:p-6">
              <h3 className="font-heading text-base font-semibold uppercase tracking-wide text-muted-foreground">
                Récapitulatif
              </h3>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Espace</span>
                  <span className="font-medium" data-testid="recap-studio">
                    {studio ? STUDIO_LABELS[studio] : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Service</span>
                  <span className="max-w-[180px] text-right font-medium" data-testid="recap-service">
                    {service ? service.name : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span className="font-medium capitalize" data-testid="recap-date">
                    {date ? format(parseISO(date), "EEE d MMM yyyy", { locale: fr }) : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Horaire</span>
                  <span className="font-mono-nums font-medium" data-testid="recap-hours">
                    {startHour !== null
                      ? `${formatHour(startHour)} – ${formatEndHour(startHour + effectiveDuration)}`
                      : "—"}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Durée</span>
                  <span className="font-mono-nums font-medium">
                    {service ? `${effectiveDuration}h` : "—"}
                  </span>
                </div>
              </div>
              {quote && (
                <>
                  <Separator className="my-4" />
                  <div className="space-y-2">
                    {(quote.addons || []).map((a) => (
                      <div key={a.slug} className="flex items-center justify-between px-1 text-xs">
                        <span className="text-muted-foreground">
                          + {a.name}
                          {a.quantity > 1 ? ` × ${a.quantity}` : ""}
                        </span>
                        <span className="font-mono-nums" data-testid={`recap-addon-${a.slug}`}>
                          {formatEuro(a.total ?? a.price)}
                        </span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between rounded-md bg-[hsl(var(--studio-yellow)/0.10)] px-3 py-2">
                      <span className="text-xs font-medium uppercase tracking-wide text-primary">
                        Total
                      </span>
                      <span className="font-mono-nums font-semibold text-primary" data-testid="recap-total-price">
                        {formatEuro(quote.amount_onsite)}
                      </span>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Même tarif en ligne ou sur place.
                    </p>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
